import { Link } from 'react-router-dom';

export default function PlanYourPodProject() {
  return (
    <section id="plan" className="relative py-32 px-8 bg-[#000000]">
      {/* Subtle pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(0,217,255,0.3) 50px, rgba(0,217,255,0.3) 51px)',
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-6xl font-thin mb-4">
            Plan Your <span className="text-cyan-400" style={{ textShadow: '0 0 30px rgba(0,217,255,0.6)' }}>Pod Project</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Pricing, approvals, site preparation and timelines vary — start with a site-specific assessment
          </p>
        </div>

        {/* Four Equal Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {[
            {
              icon: 'ri-price-tag-3-line',
              title: 'Model Pricing',
              desc: 'Each model has a different base configuration and price point. Published indicative pricing is available for most models; final pricing depends on selected options, site requirements, transport logistics and local conditions.',
            },
            {
              icon: 'ri-government-line',
              title: 'Approvals',
              desc: 'Local council permits and planning approvals vary by state, local government area, zoning, and intended use. Nexa Pods can provide documentation to support your application. Approval pathways should be confirmed before ordering.',
            },
            {
              icon: 'ri-tools-line',
              title: 'Site Preparation',
              desc: 'Site readiness — including access, ground conditions, service connections and foundation requirements — affects installation timelines and cost. A site assessment helps identify what is needed before delivery can be scheduled.',
            },
            {
              icon: 'ri-truck-line',
              title: 'Transport & Install',
              desc: 'Delivery logistics and installation duration vary by model size, destination, site accessibility and local contractor availability. Timelines are confirmed as part of project planning and are subject to site conditions.',
            },
          ].map((card, index) => (
            <div key={index} className="bg-gradient-to-br from-[#14141e]/60 to-[#0a0a0f]/60 backdrop-blur-xl border border-cyan-500/30 rounded-lg p-8 hover:border-cyan-400/60 transition-all hover:shadow-[0_0_30px_rgba(0,217,255,0.15)]">
              <div className="w-12 h-12 flex items-center justify-center mb-6">
                <i className={`${card.icon} text-4xl text-cyan-400`}></i>
              </div>
              <h3 className="text-xl font-medium text-white mb-4">{card.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{card.desc}</p>
            </div>
          ))}
        </div>

        {/* Three Column Context */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          <div className="bg-gradient-to-br from-[#14141e]/60 to-[#0a0a0f]/60 backdrop-blur-xl border border-cyan-500/30 rounded-lg p-8">
            <h3 className="text-2xl font-medium text-white mb-6">What Affects Cost</h3>
            <ul className="space-y-4">
              {[
                { icon: 'ri-checkbox-circle-line', text: 'Model and configuration selected' },
                { icon: 'ri-checkbox-circle-line', text: 'Interior fit-out and fixture choices' },
                { icon: 'ri-checkbox-circle-line', text: 'Transport distance and site accessibility' },
                { icon: 'ri-checkbox-circle-line', text: 'Site preparation and foundation works' },
                { icon: 'ri-checkbox-circle-line', text: 'Local contractor and service connection costs' },
                { icon: 'ri-checkbox-circle-line', text: 'Council application and permit fees' },
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm text-gray-400">
                  <i className={`${item.icon} text-cyan-400 flex-shrink-0 mt-0.5`}></i>
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-gradient-to-br from-[#14141e]/60 to-[#0a0a0f]/60 backdrop-blur-xl border border-cyan-500/30 rounded-lg p-8">
            <h3 className="text-2xl font-medium text-white mb-6">Potential Use Cases</h3>
            <ul className="space-y-4">
              {[
                { icon: 'ri-hotel-line', text: 'Hospitality and boutique accommodation' },
                { icon: 'ri-home-4-line', text: 'Private retreats and secondary dwellings' },
                { icon: 'ri-building-2-line', text: 'Glamping sites and eco-tourism destinations' },
                { icon: 'ri-briefcase-line', text: 'Commercial and workplace applications' },
                { icon: 'ri-compass-3-line', text: 'Remote and regional accommodation' },
                { icon: 'ri-stack-line', text: 'Multi-unit resort and village deployments' },
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 text-sm text-gray-400">
                  <i className={`${item.icon} text-cyan-400 flex-shrink-0 mt-0.5`}></i>
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-gradient-to-br from-[#14141e]/60 to-[#0a0a0f]/60 backdrop-blur-xl border border-cyan-500/30 rounded-lg p-8 flex flex-col justify-between">
            <div>
              <h3 className="text-2xl font-medium text-white mb-6">Commercial Potential</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                Revenue potential for hospitality and short-stay applications depends on location, local tourism demand, seasonality, nightly rate positioning, property management, operating costs and market competition.
              </p>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                Prospective operators should conduct their own market research and financial modelling based on their specific site and local conditions. Published indicative pricing for each model provides a starting point for budget planning.
              </p>
            </div>

            <Link 
              to="/contact"
              className="w-full px-6 py-4 bg-cyan-500 text-black font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_30px_rgba(0,217,255,0.6)] whitespace-nowrap cursor-pointer block text-center"
            >
              Request a Project Assessment
            </Link>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="text-center">
          <p className="text-xs text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Site preparation, approvals, installation requirements and final specifications vary by location and project. Pricing, timelines and revenue potential are indicative only and subject to site-specific assessment. Contact Nexa Pods for a project assessment tailored to your site and intended use. This information does not constitute financial, legal or planning advice — consult qualified professionals for decisions relating to your project.
          </p>
        </div>
      </div>
    </section>
  );
}